import React, { useState, useEffect } from 'react';

// Bird avatar component with authentic JailBirds blinking shifty eyes
const BirdAvatarWithJailbirdEyes = ({ avatarId = 1, color, accessory, size = 80, className = '' }) => {
  const [isBlinking, setIsBlinking] = useState(false);
  const [eyePosition, setEyePosition] = useState(0); // -1 = left, 0 = center, 1 = right

  useEffect(() => {
    // Blinking animation
    const scheduleNextBlink = () => {
      const delay = Math.random() * 3000 + 2000;
      setTimeout(() => {
        setIsBlinking(true);
        setTimeout(() => {
          setIsBlinking(false);
          scheduleNextBlink();
        }, 150);
      }, delay);
    };

    scheduleNextBlink();
  }, []);

  useEffect(() => {
    // Eye movement animation - suspicious looking around
    const scheduleEyeMovement = () => {
      const delay = Math.random() * 2000 + 1000;
      setTimeout(() => {
        const positions = [-1, 0, 1];
        const newPosition = positions[Math.floor(Math.random() * positions.length)];
        setEyePosition(newPosition);
        scheduleEyeMovement();
      }, delay);
    };

    scheduleEyeMovement();
  }, []);

  // Calculate eye pupil positions based on eyePosition
  const leftPupilX = 42 + (eyePosition * 2);
  const rightPupilX = 58 + (eyePosition * 2);

  const renderAccessory = () => {
    const scale = size / 80;
    
    switch (accessory) {
      case 'tiny-glasses':
        return (
          <g>
            <circle cx="42" cy="32" r={8 * scale} fill="none" stroke="#333" strokeWidth={2 * scale} />
            <circle cx="58" cy="32" r={8 * scale} fill="none" stroke="#333" strokeWidth={2 * scale} />
            <line x1="48" y1="32" x2="52" y2="32" stroke="#333" strokeWidth={2 * scale} />
          </g>
        );
      
      case 'tiny-bow':
        return (
          <g>
            <path d="M 35 20 Q 42 18 44 22" fill="#FF1493" />
            <path d="M 65 20 Q 58 18 56 22" fill="#FF1493" />
            <circle cx="50" cy="21" r="3" fill="#FF1493" />
          </g>
        );
      
      case 'heart':
        return (
          <g>
            <path d="M 50 16 L 45 12 Q 42 10 42 13 Q 42 16 50 18 Q 58 16 58 13 Q 58 10 55 12 Z" fill="#FF69B4" />
          </g>
        );
      
      case 'star':
        return (
          <g>
            <path d="M 50 10 L 52 18 L 60 19 L 54 24 L 56 32 L 50 28 L 44 32 L 46 24 L 40 19 L 48 18 Z" fill="#FFD700" />
          </g>
        );
      
      case 'cool-shades':
        return (
          <g>
            <rect x="33" y="28" width="14" height="10" rx="2" fill="#000" opacity="0.8" />
            <rect x="53" y="28" width="14" height="10" rx="2" fill="#000" opacity="0.8" />
            <line x1="47" y1="33" x2="53" y2="33" stroke="#000" strokeWidth="2" />
          </g>
        );
      
      case 'sparkle':
        return (
          <g>
            <path d="M 38 18 L 40 22 L 42 18 L 40 14 Z" fill="#FFD700" />
            <path d="M 58 14 L 60 18 L 62 14 L 60 10 Z" fill="#FFD700" />
          </g>
        );
      
      case 'sunburst':
        return (
          <g>
            <circle cx="50" cy="15" r="5" fill="#FFD700" />
            {[0, 45, 90, 135, 180, 225, 270, 315].map(angle => {
              const rad = (angle * Math.PI) / 180;
              const x1 = 50 + Math.cos(rad) * 6;
              const y1 = 15 + Math.sin(rad) * 6;
              const x2 = 50 + Math.cos(rad) * 10;
              const y2 = 15 + Math.sin(rad) * 10;
              return <line key={angle} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#FFA500" strokeWidth="2" />;
            })}
          </g>
        );
      
      case 'bunny-ears':
        return (
          <g>
            <ellipse cx="38" cy="10" rx="5" ry="12" fill={color} stroke="#FF69B4" strokeWidth="1.5" />
            <ellipse cx="62" cy="10" rx="5" ry="12" fill={color} stroke="#FF69B4" strokeWidth="1.5" />
            <ellipse cx="38" cy="10" rx="2.5" ry="7" fill="#FFC0CB" />
            <ellipse cx="62" cy="10" rx="2.5" ry="7" fill="#FFC0CB" />
          </g>
        );
      
      case 'cat-ears':
        return (
          <g>
            <path d="M 32 20 L 38 5 L 42 20 Z" fill={color} stroke="#FF69B4" strokeWidth="1.5" />
            <path d="M 68 20 L 62 5 L 58 20 Z" fill={color} stroke="#FF69B4" strokeWidth="1.5" />
            <path d="M 34 20 L 38 10 L 40 20 Z" fill="#FF69B4" />
            <path d="M 66 20 L 62 10 L 60 20 Z" fill="#FF69B4" />
          </g>
        );
      
      case 'bear-ears':
        return (
          <g>
            <circle cx="36" cy="18" r="8" fill={color} stroke="#8B4513" strokeWidth="1.5" />
            <circle cx="64" cy="18" r="8" fill={color} stroke="#8B4513" strokeWidth="1.5" />
            <circle cx="36" cy="18" r="4" fill="#DEB887" />
            <circle cx="64" cy="18" r="4" fill="#DEB887" />
          </g>
        );
      
      case 'panda':
        return (
          <g>
            <circle cx="36" cy="18" r="7" fill="#000" />
            <circle cx="64" cy="18" r="7" fill="#000" />
          </g>
        );
      
      case 'unicorn':
        return (
          <g>
            <path d="M 48 20 L 50 5 L 52 20 Z" fill="#FFD700" />
            <line x1="50" y1="8" x2="49" y2="14" stroke="#FFA500" strokeWidth="1.5" />
            <line x1="50" y1="12" x2="49" y2="18" stroke="#FFA500" strokeWidth="1.5" />
            <circle cx="40" cy="10" r="2" fill="#FFD700" />
            <circle cx="60" cy="12" r="2" fill="#FFD700" />
          </g>
        );
      
      case 'tiny-crown':
        return (
          <g>
            <path d="M 32 20 L 36 10 L 42 16 L 50 8 L 58 16 L 64 10 L 68 20 Z" fill="#FFD700" />
          </g>
        );
      
      case 'flower':
        return (
          <g>
            <circle cx="38" cy="16" r="3" fill="#FFB6C1" />
            <circle cx="42" cy="12" r="3" fill="#FFB6C1" />
            <circle cx="46" cy="16" r="3" fill="#FFB6C1" />
            <circle cx="42" cy="20" r="3" fill="#FFB6C1" />
            <circle cx="42" cy="16" r="2" fill="#FFD700" />
          </g>
        );
      
      case 'rainbow':
        return (
          <g>
            <path d="M 30 22 Q 50 10 70 22" stroke="#FF0000" strokeWidth="2" fill="none" />
            <path d="M 31 22 Q 50 11 69 22" stroke="#FFA500" strokeWidth="2" fill="none" />
            <path d="M 32 22 Q 50 12 68 22" stroke="#FFFF00" strokeWidth="2" fill="none" />
            <path d="M 33 22 Q 50 13 67 22" stroke="#00FF00" strokeWidth="2" fill="none" />
          </g>
        );
      
      case 'balloon':
        return (
          <g>
            <ellipse cx="50" cy="12" rx="6" ry="8" fill="#FF69B4" />
            <path d="M 50 20 Q 47 24 45 28" stroke="#FF1493" strokeWidth="1.5" fill="none" />
            <circle cx="49" cy="10" r="1.5" fill="#FFF" opacity="0.7" />
          </g>
        );
      
      case 'donut':
        return (
          <g>
            <circle cx="50" cy="15" r="8" fill="#FFB6C1" />
            <circle cx="50" cy="15" r="3" fill="#FFF" />
            <circle cx="47" cy="12" r="1.2" fill="#FF1493" />
            <circle cx="52" cy="12" r="1.2" fill="#4169E1" />
            <circle cx="50" cy="18" r="1.2" fill="#FFD700" />
          </g>
        );
      
      case 'ice-cream':
        return (
          <g>
            <path d="M 45 20 L 50 28 L 55 20 Z" fill="#F4A460" />
            <circle cx="50" cy="18" r="6" fill="#FFB6C1" />
            <circle cx="47" cy="14" r="4" fill="#E6E6FA" />
            <circle cx="53" cy="14" r="4" fill="#98D8C8" />
          </g>
        );
      
      case 'lollipop':
        return (
          <g>
            <circle cx="50" cy="12" r="6" fill="#FF69B4" />
            <path d="M 45 12 Q 48 9 50 12 Q 52 15 55 12" stroke="#FFF" strokeWidth="1.5" fill="none" />
            <line x1="50" y1="18" x2="50" y2="24" stroke="#8B4513" strokeWidth="2.5" />
          </g>
        );
      
      case 'strawberry':
        return (
          <g>
            <path d="M 44 20 L 50 10 L 56 20 Q 50 24 44 20" fill="#FF6B6B" />
            <path d="M 47 10 L 46 7 L 50 6 L 54 7 L 53 10" fill="#228B22" />
            <circle cx="48" cy="16" r="0.8" fill="#FFE4B5" />
            <circle cx="51" cy="15" r="0.8" fill="#FFE4B5" />
          </g>
        );
      
      case 'cookie':
        return (
          <g>
            <circle cx="50" cy="16" r="8" fill="#D2691E" />
            <circle cx="47" cy="14" r="1.5" fill="#8B4513" />
            <circle cx="53" cy="14" r="1.5" fill="#8B4513" />
            <circle cx="50" cy="19" r="1.5" fill="#8B4513" />
          </g>
        );
      
      case 'cherry':
        return (
          <g>
            <circle cx="46" cy="18" r="3" fill="#FF0000" />
            <circle cx="54" cy="19" r="3" fill="#FF0000" />
            <path d="M 46 15 Q 50 8 54 16" stroke="#228B22" strokeWidth="1.5" fill="none" />
          </g>
        );
      
      case 'butterfly':
        return (
          <g>
            <path d="M 38 16 Q 35 12 38 8 Q 41 12 40 16" fill="#DDA0DD" />
            <path d="M 62 16 Q 65 12 62 8 Q 59 12 60 16" fill="#DDA0DD" />
            <line x1="50" y1="16" x2="50" y2="8" stroke="#8B008B" strokeWidth="1.5" />
          </g>
        );
      
      case 'mustache':
        return (
          <g>
            <ellipse cx="42" cy="42" rx="6" ry="3" fill="#2C3E50" />
            <ellipse cx="58" cy="42" rx="6" ry="3" fill="#2C3E50" />
          </g>
        );
      
      case 'bandana':
        return (
          <g>
            <path d="M 30 22 L 70 22 L 67 16 L 33 16 Z" fill="#FF4444" />
            <circle cx="44" cy="19" r="1.5" fill="white" />
            <circle cx="56" cy="19" r="1.5" fill="white" />
          </g>
        );
      
      case 'astronaut':
        return (
          <g>
            <circle cx="50" cy="35" r="22" fill="none" stroke="#E0E0E0" strokeWidth="3" opacity="0.3" />
            <path d="M 32 50 Q 50 56 68 50" stroke="#E0E0E0" strokeWidth="3" fill="none" />
            <line x1="50" y1="18" x2="50" y2="10" stroke="#C0C0C0" strokeWidth="2" />
            <circle cx="50" cy="10" r="2.5" fill="#FF0000" />
          </g>
        );
      
      case 'game-controller':
        return (
          <g>
            <rect x="38" cy="14" width="24" height="10" rx="3" fill="#9370DB" />
            <circle cx="44" cy="19" r="1.5" fill="#FF0000" />
            <circle cx="56" cy="19" r="1.5" fill="#00FF00" />
          </g>
        );
      
      case 'music-note':
        return (
          <g>
            <circle cx="48" cy="22" r="3" fill="#000" />
            <rect x="48" y="10" width="2" height="12" fill="#000" />
            <path d="M 48 10 Q 56 8 56 14" stroke="#000" strokeWidth="2" fill="none" />
          </g>
        );
      
      case 'party-hat':
        return (
          <g>
            <path d="M 35 20 L 50 3 L 65 20 Z" fill="#FF1493" />
            <circle cx="50" cy="3" r="2.5" fill="#FFD700" />
          </g>
        );
      
      case 'coffee':
        return (
          <g>
            <rect x="42" y="14" width="16" height="12" rx="2" fill="#8B7355" />
            <ellipse cx="50" cy="14" rx="8" ry="2" fill="#6F4E37" />
            <path d="M 58 18 Q 62 18 62 22" stroke="#8B7355" strokeWidth="1.5" fill="none" />
            <path d="M 47 12 Q 48 9 47 8" stroke="#FFF" strokeWidth="1" fill="none" opacity="0.5" />
          </g>
        );
      
      case 'frog-hat':
        return (
          <g>
            <ellipse cx="50" cy="18" rx="16" ry="10" fill="#90EE90" />
            <circle cx="42" cy="14" r="4" fill="#FFF" />
            <circle cx="58" cy="14" r="4" fill="#FFF" />
            <circle cx="42" cy="14" r="2.5" fill="#000" />
            <circle cx="58" cy="14" r="2.5" fill="#000" />
          </g>
        );
      
      case 'witch-hat':
        return (
          <g>
            <ellipse cx="50" cy="20" rx="20" ry="3" fill="#4B0082" />
            <path d="M 38 20 L 50 2 L 62 20" fill="#4B0082" />
            <rect x="34" y="18" width="32" height="3" fill="#FF8C00" />
          </g>
        );
      
      case 'headphones':
        return (
          <g>
            <path d="M 30 32 Q 50 10 70 32" stroke="#FFB6C1" strokeWidth="4" fill="none" />
            <rect x="28" y="30" width="8" height="12" rx="3" fill="#FFB6C1" />
            <rect x="64" y="30" width="8" height="12" rx="3" fill="#FFB6C1" />
          </g>
        );
      
      case 'sushi':
        return (
          <g>
            <ellipse cx="50" cy="16" rx="10" ry="6" fill="#FFF" />
            <ellipse cx="50" cy="16" rx="5" ry="3" fill="#FF6B6B" />
            <rect x="40" y="13" width="20" height="3" fill="#1a1a1a" />
          </g>
        );
      
      case 'avocado':
        return (
          <g>
            <ellipse cx="50" cy="16" rx="8" ry="10" fill="#C1E1C1" />
            <ellipse cx="50" cy="16" rx="5" ry="6" fill="#F4E5C3" />
            <circle cx="50" cy="16" r="3" fill="#8B4513" />
          </g>
        );
      
      case 'cactus':
        return (
          <g>
            <rect x="47" y="12" width="6" height="16" rx="3" fill="#228B22" />
            <rect x="42" y="18" width="5" height="8" rx="2" fill="#228B22" />
            <rect x="53" y="16" width="5" height="8" rx="2" fill="#228B22" />
            <circle cx="50" cy="22" r="0.8" fill="#FF69B4" />
          </g>
        );
      
      case 'pizza':
        return (
          <g>
            <path d="M 42 22 L 50 8 L 58 22 Z" fill="#FFD700" />
            <circle cx="47" cy="17" r="1.8" fill="#FF4500" />
            <circle cx="52" cy="17" r="1.8" fill="#FF4500" />
            <circle cx="50" cy="13" r="1.8" fill="#FF4500" />
          </g>
        );
      
      case 'moon':
        return (
          <g>
            <circle cx="50" cy="15" r="8" fill="#FFF9C4" />
            <circle cx="53" cy="15" r="6" fill={color} />
            <circle cx="46" cy="13" r="1.2" fill="#FFD700" />
          </g>
        );
      
      case 'present':
        return (
          <g>
            <rect x="42" y="14" width="16" height="12" rx="2" fill="#FF69B4" />
            <rect x="49" y="14" width="2" height="12" fill="#FFD700" />
            <path d="M 45 12 Q 50 6 55 12" fill="#FFD700" />
          </g>
        );
      
      case 'snowflake':
        return (
          <g>
            <line x1="50" y1="6" x2="50" y2="22" stroke="#B0E0E6" strokeWidth="2" />
            <line x1="40" y1="14" x2="60" y2="14" stroke="#B0E0E6" strokeWidth="2" />
            <line x1="44" y1="9" x2="56" y2="19" stroke="#B0E0E6" strokeWidth="2" />
            <line x1="56" y1="9" x2="44" y2="19" stroke="#B0E0E6" strokeWidth="2" />
          </g>
        );
      
      case 'bee-wings':
        return (
          <g>
            <ellipse cx="38" cy="20" rx="8" ry="10" fill="#FFF" opacity="0.6" stroke="#FFD700" strokeWidth="1.5" />
            <ellipse cx="62" cy="20" rx="8" ry="10" fill="#FFF" opacity="0.6" stroke="#FFD700" strokeWidth="1.5" />
            <rect x="48" y="40" width="4" height="5" fill="#FFD700" />
            <rect x="48" y="46" width="4" height="3" fill="#000" />
          </g>
        );
      
      case 'lightning':
        return (
          <g>
            <path d="M 52 8 L 46 18 L 51 18 L 45 28 L 54 19 L 51 19 Z" fill="#FFD700" />
          </g>
        );
      
      default:
        return null;
    }
  };

  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 100 100" 
      className={className}
      style={{ display: 'inline-block' }}
    >
      {/* Body - egg shaped */}
      <ellipse cx="50" cy="55" rx="28" ry="32" fill={color} />
      {/* Head merged with body */}
      <circle cx="50" cy="35" r="20" fill={color} />
      
      {/* Accessories BEHIND eyes */}
      {renderAccessory()}
      
      {/* Eyes - ALWAYS ON TOP with JailBirds blinking animation */}
      {isBlinking ? (
        // Closed eyes (horizontal lines)
        <>
          <path d="M 35 32 Q 42 33 49 32" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M 51 32 Q 58 33 65 32" stroke="#000" strokeWidth="3" fill="none" strokeLinecap="round" />
        </>
      ) : (
        // Open eyes with moving pupils
        <>
          {/* Left eye */}
          <circle cx="42" cy="32" r="7" fill="#FFF" />
          <circle cx={leftPupilX} cy="33" r="5" fill="#000" style={{ transition: 'cx 0.3s ease' }} />
          <circle cx={leftPupilX - 2} cy="31" r="2" fill="#FFF" />
          
          {/* Right eye */}
          <circle cx="58" cy="32" r="7" fill="#FFF" />
          <circle cx={rightPupilX} cy="33" r="5" fill="#000" style={{ transition: 'cx 0.3s ease' }} />
          <circle cx={rightPupilX - 2} cy="31" r="2" fill="#FFF" />
        </>
      )}
      
      {/* Tiny beak */}
      <path 
        d="M 50 38 L 48 42 L 52 42 Z" 
        fill="#FFA500" 
        stroke="#000" 
        strokeWidth="1"
      />
    </svg>
  );
};

export default BirdAvatarWithJailbirdEyes;
